import { useState, useEffect, useMemo } from "react";
import type { ReactNode } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { grey } from "@mui/material/colors";
import type { BaseTransfert } from "models";
import { porteesUtil } from "utils";
import PorteeAccordionDetailWithIcon from "./PorteeAccordionDetailWithIcon";

interface EauAssGepuPorteesAccordionProps {
  transferts: Array<BaseTransfert>;
  porteeIds?: Array<number>;
  porteeAdditionnellesIds?: Array<number>;
  sousCompetenceId: string;
}

function EauAssGepuPorteesAccordion({
  transferts,
  porteeIds,
  porteeAdditionnellesIds,
  sousCompetenceId,
}: Readonly<EauAssGepuPorteesAccordionProps>): ReactNode {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [competence, setCompetence] = useState<"EAU" | "ASS">(
    porteesUtil.getCompetence(transferts[0]) as "EAU" | "ASS"
  );

  useEffect(() => {
    const comp = porteesUtil.getCompetence(transferts[0]) as "EAU" | "ASS";
    setCompetence(comp);
  }, [transferts]);

  const competenceAndAdhesionLookup: Record<
    number,
    { compCode: string; isPorteeAdheree: boolean }
  > = useMemo(
    () =>
      [...(porteeIds ?? []), ...(porteeAdditionnellesIds ?? [])].reduce(
        (acc: Record<number, { compCode: string; isPorteeAdheree: boolean }>, porteeId) => {
          const compCode = `${competence}-${sousCompetenceId}-${porteeId}`;
          const isPorteeAdheree = transferts.some((t) => t.code === compCode);
          acc[porteeId] = { compCode, isPorteeAdheree };
          return acc;
        },
        {}
      ),
    [porteeIds, porteeAdditionnellesIds, competence, sousCompetenceId, transferts]
  );

  if (porteeIds == null && porteeAdditionnellesIds == null) {
    return null;
  }

  return (
    <Grid size={12}>
      <Accordion
        sx={{ width: "100%", border: "none", background: "transparent" }}
        disableGutters
        variant="outlined"
        expanded={isAccordionOpen}
        onChange={() => {
          setIsAccordionOpen(!isAccordionOpen);
        }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ flexDirection: "row-reverse", background: "transparent" }}>
          <Typography sx={{ ml: 1 }}>
            {!isAccordionOpen &&
              porteeIds?.map((porteeId, i) => {
                const { compCode, isPorteeAdheree } = competenceAndAdhesionLookup[porteeId];
                return (
                  <Typography
                    key={porteeId}
                    color={isPorteeAdheree ? undefined : grey[400]}
                    component="span">
                    {i > 0 && ","} {porteesUtil.getPorteeLabel(compCode)}
                  </Typography>
                );
              })}
            {!isAccordionOpen &&
              porteeAdditionnellesIds?.map((porteeId) => {
                const { compCode, isPorteeAdheree } = competenceAndAdhesionLookup[porteeId];
                if (!isPorteeAdheree) {
                  return null;
                }
                return (
                  <Typography key={porteeId} component="span">
                    , {porteesUtil.getPorteeLabel(compCode)}
                  </Typography>
                );
              })}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ marginTop: -6.5, marginLeft: 3, paddingBottom: 0 }}>
          <Grid container>
            {porteeIds?.map((porteeId) => {
              const { compCode, isPorteeAdheree } = competenceAndAdhesionLookup[porteeId];
              return (
                <PorteeAccordionDetailWithIcon
                  size={12}
                  key={porteeId}
                  marginLeft={1}
                  isPorteeAdheree={isPorteeAdheree}
                  transfert={compCode}
                />
              );
            })}
            {porteeAdditionnellesIds?.map((porteeId) => {
              const { compCode, isPorteeAdheree } = competenceAndAdhesionLookup[porteeId];
              return (
                isPorteeAdheree && (
                  <PorteeAccordionDetailWithIcon
                    size={12}
                    key={porteeId}
                    marginLeft={1}
                    isPorteeAdheree={isPorteeAdheree}
                    transfert={compCode}
                  />
                )
              );
            })}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
}

export default EauAssGepuPorteesAccordion;
